import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/common/PageHero";
import PageTransition from "@/components/layout/PageTransition";
import AnimatedSection from "@/components/common/AnimatedSection";
import { pageHeroImages } from "@/config/site";
import { api } from "@/lib/api";

export default function PapersPage() {
  const [papers, setPapers] = useState([]);

  useEffect(() => {
    const loadPapers = async () => {
      try {
        const response = await api.get("/public/papers");
        setPapers(response.data);
      } catch {
        toast.error("Unable to load published papers");
      }
    };

    loadPapers();
  }, []);

  return (
    <PageTransition testId="papers-page">
      <PageHero
        title="Published Research & Papers"
        subtitle="Clinical insights and scientific contributions by our doctors in prosthodontics and advanced dentistry."
        image={pageHeroImages.papers}
        testId="papers-page-hero"
      />

      <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-24">
        <AnimatedSection testId="papers-list-section">
          <h2
            className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-5xl"
            data-testid="papers-list-title"
          >
            Research from Kavlekar Dental Team
          </h2>

          <div className="mt-8 space-y-5">
            {papers.map((paper, index) => (
              <motion.article
                key={paper.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
                className="rounded-2xl border border-[#f2e6c9] bg-white p-6 shadow-[0_8px_22px_rgba(0,0,0,0.05)]"
                data-testid={`paper-card-${paper.id}`}
              >
                <p
                  className="font-[Manrope] text-xs uppercase tracking-[0.16em] text-[#8b7a55]"
                  data-testid={`paper-journal-${paper.id}`}
                >
                  {paper.journal} · {paper.published_on}
                </p>
                <h3
                  className="mt-2 font-[Playfair_Display] text-2xl text-[#1a1a1a]"
                  data-testid={`paper-title-${paper.id}`}
                >
                  {paper.title}
                </h3>
                <p
                  className="mt-2 font-[Manrope] text-sm text-[#555555]"
                  data-testid={`paper-authors-${paper.id}`}
                >
                  Authors: {paper.authors.join(", ")}
                </p>
                <p
                  className="mt-3 font-[Manrope] text-sm text-[#555555]"
                  data-testid={`paper-abstract-${paper.id}`}
                >
                  {paper.abstract}
                </p>
                <a
                  href={paper.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#c5a059] px-5 py-2.5 font-[Manrope] text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#b08d45]"
                  data-testid={`paper-download-button-${paper.id}`}
                >
                  <Download className="h-4 w-4" /> Download PDF
                </a>
              </motion.article>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </PageTransition>
  );
}
